// `FindReferencesResultGroup[]` → Monaco `Location[]`.
//
// One synthesized model per referencing object (see `peekReferenceModels.ts`),
// one `Location` per `ReferenceRecord`. The record's `SqlRange` is 0-indexed
// against the object's `bodyText` — exactly the text we push into the model —
// so the conversion is a straight +1 on line/column.
//
// Bodies are pulled through the SAME per-engine introspection feed the
// ReferencesTab uses (`fetchObjectBody` / `fetchProceduralObjectBody`), so no
// new IPC surface and no re-derivation of occurrences: the index already knows
// where every reference sits.
//
// A group whose body cannot be fetched (engine has no DDL surface for the
// kind, object dropped between index build and peek) is skipped — peek can
// only point at text it can show. Those objects remain visible through the
// "Find All References in Tab" surface, which renders range-only rows.
//
// Loading is CANCELLABLE (`isCancelled`): the loop is the gesture's second and
// longer asynchronous window, so the caller's invocation guard is asked between
// bodies as well as after the index wait — see the field's note below.

import type * as monacoNs from 'monaco-editor';

import type { DatabaseEngineId } from '@/core/engines';

import { fetchObjectBody, fetchProceduralObjectBody } from '../index/objectBodyFetcher';
import type {
  FindReferencesResultGroup,
  ObjectKind,
  QualifiedName,
  SqlRange,
} from '../types';

import {
  ensurePeekReferenceModel,
  type PeekModelMonacoApi,
} from './peekReferenceModels';

export type ObjectBodyLoader = (args: {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
}) => Promise<string | null>;

export type BuildPeekLocationsArgs = {
  readonly monaco: PeekModelMonacoApi;
  readonly languageId: string;
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly groups: readonly FindReferencesResultGroup[];
  /** Injected in unit tests; defaults to the per-engine introspection feed. */
  readonly loadBody?: ObjectBodyLoader;
  /** Is the gesture that asked for these locations still the one being
   *  answered? Asked before every body load and again the moment one arrives —
   *  body loading is one IPC round trip per referencing object, which is ample
   *  room for the caller's invocation context to move (a tab switched from
   *  connection A to B). A cancelled run stops loading, mints NO further
   *  models — a model created for a gesture nobody waits on still occupies its
   *  `jamsql-ref:` URI — and yields no locations at all, so a caller that
   *  forgets to re-check cannot hand Monaco the old context's answer. Models
   *  already minted before the cancellation stay: they belong to the
   *  connection/database they were minted for, are keyed by that URI (a later
   *  peek reuses or refreshes them) and are disposed with that connection by
   *  `disposePeekReferenceModelsForConnection`.
   *  Defaults to "still wanted". */
  readonly isCancelled?: () => boolean;
};

export async function buildPeekReferenceLocations(
  args: BuildPeekLocationsArgs,
): Promise<monacoNs.languages.Location[]> {
  const loadBody = args.loadBody ?? defaultLoadBody;
  const isCancelled = args.isCancelled ?? (() => false);
  const locations: monacoNs.languages.Location[] = [];

  for (const group of args.groups) {
    if (isCancelled()) return [];
    let bodyText: string | null = null;
    try {
      bodyText = await loadBody({
        connectionId: args.connectionId,
        database: args.database,
        engineId: args.engineId,
        objectKind: group.objectKind,
        objectName: group.objectName,
      });
    } catch {
      bodyText = null;
    }
    // The body arrived; the context may have moved while it was in flight.
    if (isCancelled()) return [];
    if (bodyText === null) continue;

    const entry = ensurePeekReferenceModel({
      monaco: args.monaco,
      languageId: args.languageId,
      connectionId: args.connectionId,
      database: args.database,
      engineId: args.engineId,
      objectKind: group.objectKind,
      objectName: group.objectName,
      bodyText,
    });

    for (const ref of group.references) {
      locations.push({ uri: entry.model.uri, range: sqlRangeToMonacoRange(ref.range) });
    }
  }

  return locations;
}

/** 0-indexed `SqlRange` → 1-indexed Monaco `IRange`. Mirrors the boundary
 *  conversion the definition provider already uses. */
export function sqlRangeToMonacoRange(range: SqlRange): monacoNs.IRange {
  return {
    startLineNumber: range.startLine + 1,
    startColumn: range.startColumn + 1,
    endLineNumber: range.endLine + 1,
    endColumn: range.endColumn + 1,
  };
}

const defaultLoadBody: ObjectBodyLoader = (args) =>
  args.objectKind === 'procedure' || args.objectKind === 'trigger'
    ? fetchProceduralObjectBody(args)
    : fetchObjectBody(args);
